/**
 * VideoCall - Composants d'appel vidéo WebRTC
 * Utilise simple-peer pour la gestion des connexions P2P
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { Dialog, DialogContent } from '../../ui/dialog';
import { api } from '../../../services/api';
import { toast } from 'sonner';

// Configuration des serveurs ICE pour WebRTC
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ]
};

/**
 * Interface utilisateur partagée pour un appel vidéo actif
 */
export function ActiveVideoCallUI({ 
  localStream, 
  remoteStream, 
  partnerName, 
  partnerAvatar, 
  callStatus, 
  onEndCall,
  isDarkMode 
}) {
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Attacher les streams aux éléments vidéo
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Timer de durée
  useEffect(() => {
    if (callStatus === 'active') {
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [callStatus]);

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={true} onOpenChange={() => onEndCall()}>
      <DialogContent className="sm:max-w-2xl bg-gray-900 border-gray-700 p-0 overflow-y-auto" data-testid="video-call-dialog">
        <div className="relative h-[500px] bg-black">
          {/* Vidéo distante (plein écran) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
            data-testid="remote-video"
          />
          
          {/* Overlay si en attente */}
          {callStatus !== 'active' && (
            <div className="absolute inset-0 bg-gray-900/80 flex flex-col items-center justify-center">
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="text-6xl mb-4"
              >
                {partnerAvatar || '📹'}
              </motion.div>
              <div className="text-white text-xl font-semibold mb-2">{partnerName}</div>
              <div className="text-gray-400">
                {callStatus === 'connecting' && (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Connexion...
                  </div>
                )}
                {callStatus === 'ringing' && (
                  <div className="flex items-center gap-2">
                    <div className="animate-pulse">🔔</div>
                    Appel en cours...
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Vidéo locale (PIP) */}
          <div className="absolute top-4 right-4 w-32 h-24 bg-gray-800 rounded-xl overflow-hidden shadow-lg border-2 border-gray-600">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
              data-testid="local-video"
            />
            {isVideoOff && (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <VideoOff className="w-8 h-8" />
              </div>
            )}
          </div>
          
          {/* Durée de l'appel */}
          {callStatus === 'active' && (
            <div className="absolute top-4 left-4 bg-red-500/80 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              {formatDuration(duration)}
            </div>
          )}
          
          {/* Nom du partenaire en appel */}
          {callStatus === 'active' && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-1 rounded-full text-sm">
              {partnerName}
            </div>
          )}
          
          {/* Contrôles */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleMute}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isMuted ? 'bg-red-500' : 'bg-gray-700 hover:bg-gray-600'
              }`}
              data-testid="toggle-mute-btn"
            >
              {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isVideoOff ? 'bg-red-500' : 'bg-gray-700 hover:bg-gray-600'
              }`}
              data-testid="toggle-video-btn"
            >
              {isVideoOff ? <VideoOff className="w-6 h-6 text-white" /> : <Video className="w-6 h-6 text-white" />}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onEndCall}
              className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all"
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-6 h-6 text-white" />
            </motion.button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal d'appel vidéo sortant (initiateur)
 */
export function VideoCallModal({ isDarkMode, customerId, matchId, partnerName, partnerAvatar, onClose }) {
  const [callStatus, setCallStatus] = useState('connecting');
  const [callId, setCallId] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const peerRef = useRef(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    startCall();
    return () => cleanupCall();
  }, []);

  const startCall = async () => {
    try {
      // Obtenir le flux média local
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);

      // Import dynamique de simple-peer
      const { default: Peer } = await import('simple-peer');

      // Créer le peer initiateur avec simple-peer
      const peer = new Peer({
        initiator: true,
        trickle: false,
        stream: stream,
        config: ICE_SERVERS
      });

      peer.on('signal', async (signalData) => {
        try {
          setCallStatus('ringing');
          const result = await api.initiateVideoCall(customerId, matchId, btoa(JSON.stringify(signalData)));
          setCallId(result.callId);
          startPolling(result.callId, peer);
        } catch (error) {
          console.error('Erreur initiation appel:', error);
          toast.error('Impossible d\'initier l\'appel');
          cleanupCall();
          onClose?.();
        }
      });

      peer.on('stream', (remoteStreamData) => {
        console.log('📹 Stream distant reçu!');
        setRemoteStream(remoteStreamData);
        setCallStatus('active');
      });

      peer.on('error', (err) => {
        console.error('Erreur peer:', err);
        toast.error('Erreur de connexion');
        cleanupCall();
        onClose?.();
      });

      peer.on('close', () => {
        console.log('Peer fermé');
        cleanupCall();
        onClose?.();
      });

      peerRef.current = peer;

    } catch (error) {
      console.error('Erreur appel vidéo:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Veuillez autoriser l\'accès à la caméra et au micro');
      } else {
        toast.error('Impossible de démarrer l\'appel vidéo');
      }
      onClose?.();
    }
  };

  const startPolling = (id, peer) => {
    const checkStatus = async () => {
      try {
        const status = await api.getVideoCallStatus(id);
        
        if (status.status === 'active' && status.answer) {
          const answer = JSON.parse(atob(status.answer));
          peer.signal(answer);
          clearInterval(pollingRef.current);
          return;
        } else if (status.status === 'rejected' || status.status === 'ended') {
          toast.info(status.status === 'rejected' ? 'Appel refusé' : 'Appel terminé');
          cleanupCall();
          onClose?.();
          clearInterval(pollingRef.current);
          return;
        }
      } catch (error) {
        console.error('Erreur polling:', error);
      }
    };

    pollingRef.current = setInterval(checkStatus, 1500);
  };

  const cleanupCall = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (localStream) localStream.getTracks().forEach(track => track.stop());
    if (peerRef.current) peerRef.current.destroy();
  };

  const endCall = async () => {
    if (callId) {
      try {
        await api.endVideoCall(callId, customerId);
      } catch (error) {}
    }
    cleanupCall();
    onClose?.();
  };

  return (
    <ActiveVideoCallUI
      localStream={localStream}
      remoteStream={remoteStream}
      partnerName={partnerName}
      partnerAvatar={partnerAvatar}
      callStatus={callStatus}
      onEndCall={endCall}
      isDarkMode={isDarkMode}
    />
  );
}

/**
 * Modal d'appel vidéo entrant (récepteur)
 */
export function IncomingCallModal({ isDarkMode, callData, customerId, onAccept, onReject }) {
  const [accepting, setAccepting] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callStatus, setCallStatus] = useState('ringing');
  const peerRef = useRef(null);

  const handleAccept = async () => {
    if (accepting) return;
    setAccepting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);

      const { default: Peer } = await import('simple-peer');

      const peer = new Peer({
        initiator: false,
        trickle: false,
        stream: stream,
        config: ICE_SERVERS
      });

      peer.on('signal', async (signalData) => {
        try {
          await api.answerVideoCall(callData.callId, customerId, btoa(JSON.stringify(signalData)));
          setCallStatus('connecting');
        } catch (error) {
          console.error('Erreur réponse appel:', error);
          toast.error('Erreur lors de la connexion');
        }
      });

      peer.on('stream', (remoteStreamData) => {
        console.log('📹 Stream distant reçu!');
        setRemoteStream(remoteStreamData);
        setCallStatus('active');
      });

      peer.on('error', (err) => {
        console.error('Erreur peer:', err);
        toast.error('Erreur de connexion');
        handleCleanupAndClose();
      });

      peer.on('close', () => {
        console.log('Peer fermé');
        handleCleanupAndClose();
      });

      peerRef.current = peer;

      if (callData.offer) {
        const offer = JSON.parse(atob(callData.offer));
        peer.signal(offer);
      }

      setIsAccepted(true);
      toast.success('📹 Appel connecté !');

    } catch (error) {
      console.error('Erreur acceptation appel:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Veuillez autoriser l\'accès à la caméra et au micro');
      } else {
        toast.error('Impossible de répondre à l\'appel');
      }
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    try {
      await api.rejectVideoCall(callData.callId, customerId);
    } catch (error) {}
    onReject?.();
  };

  const handleCleanupAndClose = () => {
    if (localStream) localStream.getTracks().forEach(track => track.stop());
    if (peerRef.current) peerRef.current.destroy();
    onReject?.();
  };

  const handleEndCall = async () => {
    try {
      await api.endVideoCall(callData.callId, customerId);
    } catch (error) {}
    handleCleanupAndClose();
  };

  if (isAccepted) {
    return (
      <ActiveVideoCallUI
        localStream={localStream}
        remoteStream={remoteStream}
        partnerName={callData.callerName}
        partnerAvatar={callData.callerAvatar}
        callStatus={callStatus}
        onEndCall={handleEndCall}
        isDarkMode={isDarkMode}
      />
    );
  }

  return (
    <Dialog open={true} onOpenChange={handleReject}>
      <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white'}`} data-testid="incoming-call-dialog">
        <div className="text-center py-6">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="text-6xl mb-4"
          >
            {callData.callerAvatar || '📹'}
          </motion.div>
          
          <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {callData.callerName}
          </h3>
          
          <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            📹 Appel vidéo entrant...
          </p>
          
          <div className="flex justify-center gap-6">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleReject}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
              data-testid="reject-call-btn"
            >
              <PhoneOff className="w-7 h-7 text-white" />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleAccept}
              disabled={accepting}
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center"
              data-testid="accept-call-btn"
            >
              {accepting ? (
                <div className="animate-spin w-7 h-7 border-3 border-white border-t-transparent rounded-full" />
              ) : (
                <Phone className="w-7 h-7 text-white" />
              )}
            </motion.button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default { ActiveVideoCallUI, VideoCallModal, IncomingCallModal };
